import React, { useState, useEffect } from 'react';
import { MessageCircle, CheckCircle, RefreshCw } from 'lucide-react';
import apiService from '../services/api';

interface PhoneVerificationProps {
  phoneNumber?: string;
  isVerified?: boolean;
  onVerificationComplete: () => void;
}

const PhoneVerification: React.FC<PhoneVerificationProps> = ({
  phoneNumber,
  isVerified = false,
  onVerificationComplete,
}) => {
  const [verificationCode, setVerificationCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSendingCode, setIsSendingCode] = useState(false);
  const [message, setMessage] = useState<{
    type: 'success' | 'error';
    text: string;
  } | null>(null);
  const [verificationStatus, setVerificationStatus] = useState<{
    phoneNumber?: string;
    phoneNumberVerified: boolean;
    canResend: boolean;
  } | null>(null);

  useEffect(() => {
    if (phoneNumber) {
      loadVerificationStatus();
    }
  }, [phoneNumber]);

  const loadVerificationStatus = async () => {
    try {
      const status = await apiService.getPhoneVerificationStatus();
      setVerificationStatus(status);
    } catch (error) {
      console.error('Failed to load verification status:', error);
    }
  };

  const handleSendVerification = async () => {
    if (!phoneNumber) {
      setMessage({ type: 'error', text: 'Please enter a phone number first' });
      return;
    }

    setIsSendingCode(true);
    setMessage(null);

    try {
      await apiService.sendPhoneVerification(phoneNumber);
      setMessage({
        type: 'success',
        text: 'Verification code sent! Check your phone for the SMS.',
      });
      await loadVerificationStatus(); // Refresh status
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.message || 'Failed to send verification code';
      setMessage({ type: 'error', text: errorMessage });
    } finally {
      setIsSendingCode(false);
    }
  };

  const handleVerifyCode = async () => {
    if (!verificationCode.trim()) {
      setMessage({ type: 'error', text: 'Please enter the verification code' });
      return;
    }

    setIsLoading(true);
    setMessage(null);

    try {
      const result = await apiService.verifyPhoneCode(verificationCode);
      if (result.verified) {
        setMessage({
          type: 'success',
          text: 'Phone number verified successfully!',
        });
        setVerificationCode('');
        await loadVerificationStatus(); // Refresh status
        onVerificationComplete(); // Notify parent component
      }
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.message || 'Failed to verify code';
      setMessage({ type: 'error', text: errorMessage });
    } finally {
      setIsLoading(false);
    }
  };

  const formatPhoneNumber = (phone: string) => {
    const cleaned = phone.replace(/\D/g, '');
    if (cleaned.length === 10) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
    }
    return phone;
  };

  if (!phoneNumber) {
    return null;
  }

  return (
    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
      <div className="flex items-start space-x-3">
        <MessageCircle className="h-5 w-5 text-blue-600 mt-0.5" />
        <div className="flex-1">
          <h3 className="text-sm font-medium text-blue-900">
            Phone Number Verification
          </h3>
          <p className="text-sm text-blue-700 mt-1">
            {formatPhoneNumber(phoneNumber)}
            {isVerified && (
              <span className="inline-flex items-center ml-2 text-green-600">
                <CheckCircle className="h-4 w-4 mr-1" />
                Verified
              </span>
            )}
          </p>

          {message && (
            <div
              className={`mt-3 p-3 rounded-md text-sm ${
                message.type === 'success'
                  ? 'bg-green-100 border border-green-200 text-green-800'
                  : 'bg-red-100 border border-red-200 text-red-800'
              }`}
            >
              {message.text}
            </div>
          )}

          {!isVerified && (
            <div className="mt-4 space-y-3">
              {verificationStatus && !verificationStatus.canResend && (
                <p className="text-sm text-blue-600">
                  Please wait before requesting another code.
                </p>
              )}

              <div className="flex space-x-2">
                <button
                  onClick={handleSendVerification}
                  disabled={
                    isSendingCode || verificationStatus?.canResend === false
                  }
                  className="inline-flex items-center px-3 py-2 border border-blue-300 shadow-sm text-sm leading-4 font-medium rounded-md text-blue-700 bg-white hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSendingCode ? (
                    <>
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <MessageCircle className="h-4 w-4 mr-2" />
                      Send Code
                    </>
                  )}
                </button>
              </div>

              <div className="flex space-x-2">
                <input
                  type="text"
                  value={verificationCode}
                  onChange={(e) =>
                    setVerificationCode(
                      e.target.value.replace(/\D/g, '').slice(0, 6),
                    )
                  }
                  placeholder="Enter 6-digit code"
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  maxLength={6}
                />
                <button
                  onClick={handleVerifyCode}
                  disabled={isLoading || verificationCode.length !== 6}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? (
                    <>
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                      Verifying...
                    </>
                  ) : (
                    'Verify'
                  )}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PhoneVerification;
